/**
 * Feature flags derived from Vite environment variables.
 *
 * Two kinds of flag live here:
 *
 *  - **Build-time flags** (e.g. `enableFuture`): the `=== 'true'` comparison is
 *    folded to a constant by Vite at build time. Their value is fixed when the
 *    image is built and cannot be changed at container runtime.
 *
 *  - **Runtime flags** (e.g. `showPreflightButton`): the raw env var is read
 *    into a module-level string so Vite preserves the `__RUNTIME_VITE_*__`
 *    placeholder verbatim in the bundle; the entrypoint replaces it via `sed`
 *    at container startup (RUNTIME_VITE_* -> placeholder). The `=== 'true'`
 *    comparison must therefore happen at ACCESS time (a getter), NOT at module
 *    init, or Vite would constant-fold it and drop the placeholder. This is the
 *    same mechanism the Auth0 string vars use.
 *
 * A flag is enabled only when its variable is exactly the string "true".
 *
 * Usage:
 *   import { flags } from '@/lib/flags';
 *   if (flags.enableFuture) { ... }
 *   if (flags.showPreflightButton) { ... }
 *
 * To enable locally, add to your .env.local (apps/management-plane-ui):
 *   VITE_ENABLE_FUTURE=true
 *   VITE_SHOW_PREFLIGHT_BUTTON=true
 */

// Read as a raw string (no comparison here) so Vite keeps the placeholder in the
// bundle for runtime sed replacement instead of folding it to a boolean.
const rawShowPreflightButton: string = import.meta.env.VITE_SHOW_PREFLIGHT_BUTTON ?? '';
// Preflight-on-install controls (runtime). Read as raw strings for the same
// reason; the getters apply the default/comparison at access time.
const rawRunPreflightOnWorkspaceInstall: string =
  import.meta.env.VITE_RUN_PREFLIGHT_ON_WORKSPACE_INSTALL ?? '';
const rawRunPreflightOnDeploymentCreate: string =
  import.meta.env.VITE_RUN_PREFLIGHT_ON_DEPLOYMENT_CREATE ?? '';

export const flags = {
  /** Gates features that are not yet ready for general availability. (build-time) */
  enableFuture: import.meta.env.VITE_ENABLE_FUTURE === 'true',
  /**
   * Shows the standalone "Run preflight" button on the deployment page.
   * Runtime-evaluated (getter) so RUNTIME_VITE_SHOW_PREFLIGHT_BUTTON can toggle
   * it without rebuilding the image. The comparison stays inside the getter (not
   * at module init) so Vite cannot constant-fold it and drop the placeholder.
   */
  get showPreflightButton(): boolean {
    return rawShowPreflightButton.trim() === 'true';
  },
  /**
   * When true, creating a workspace runs preflight first and gates on the result
   * (the checklist dialog). When false, no preflight runs and create proceeds
   * directly with no gating. Runtime-evaluated; default false. (runtime)
   */
  get runPreflightOnWorkspaceInstall(): boolean {
    return rawRunPreflightOnWorkspaceInstall.trim() === 'true';
  },
  /**
   * When true, creating a deployment runs the pre-deploy-deployment preflight
   * first and gates on the result (the checklist dialog). Preflight can only run
   * when a target account ID is supplied and Sunstone's role is assumable there;
   * when it can't run, creation proceeds directly. When false, no preflight runs.
   * Runtime-evaluated; default false. (runtime)
   */
  get runPreflightOnDeploymentCreate(): boolean {
    return rawRunPreflightOnDeploymentCreate.trim() === 'true';
  },
} as const;
